import React, { useEffect, useState } from 'react';
import StartNonprofit from '../../components/portal/StartNonprofit';
import axios from 'axios';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import VenturesPanel from '../admin/VenturesPanel';
import ClientJumpstart from '../../components/coaching/ClientJumpstart';
import WebsiteVisionPanel from '../../components/WebsiteVisionPanel';
import ToolGate from '../../components/portal/ToolGate';
import {
  Compass, Briefcase, HeartHandshake, RefreshCw, CheckCircle2, Circle, FileText,
  Rocket, ClipboardCheck, Lightbulb, ArrowRight, Globe,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const Ring = ({ pct, color = '#C026D3', size = 52 }) => {
  const r = 20, c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 48 48" className="w-full h-full -rotate-90">
        <circle cx="24" cy="24" r={r} fill="none" stroke="#EEF2F7" strokeWidth="5" />
        <circle cx="24" cy="24" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[11px] font-black text-[#0A0F1C]">{pct}%</div>
    </div>
  );
};

export default function ClientClarityHub() {
  const [data, setData] = useState(null);
  const [insight, setInsight] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/portal/clarity-hub');
        setData(r.data);
        if ((r.data.business?.total || 0) > 0 || (r.data.nonprofit?.projects?.length || 0) > 0) {
          api.get('/api/portal/clarity-hub/insight').then((ir) => setInsight(ir.data.insight || '')).catch(() => {});
        }
      } catch (e) { toast.error('Could not load your Clarity Hub: ' + (e.response?.data?.detail || e.message)); }
      finally { setLoading(false); }
    })();
  }, []);

  const biz = data?.business;
  const np = data?.nonprofit;

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="client-clarity-hub">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1D4ED8] to-fuchsia-500 flex items-center justify-center text-white"><Compass size={20} /></span>
          <div>
            <h1 className="text-2xl font-black text-[#0A0F1C] tracking-tight">Your Clarity Hub</h1>
            <p className="text-sm text-[#64748B]">Build your business (and nonprofit) vision on your own time — your strategist sees your progress and helps you refine it.</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 text-center text-sm text-[#64748B]"><RefreshCw className="animate-spin inline mr-2" size={18} /> Loading…</div>
        ) : !data ? null : (
          <>
            {/* Your next steps */}
            <div className="mt-5 rounded-2xl border border-[#BFDBFE] bg-gradient-to-r from-[#EFF6FF] to-white p-5" data-testid="client-steps">
              <div className="text-[10px] font-black uppercase tracking-wider text-[#1D4ED8] mb-2">Your next steps</div>
              <ul className="space-y-2">
                {data.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#334155]"><ArrowRight size={15} className="text-[#1D4ED8] shrink-0 mt-0.5" /> {s}</li>
                ))}
              </ul>
            </div>

            {insight && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-4 flex items-start gap-3" data-testid="client-insight">
                <span className="shrink-0 w-9 h-9 rounded-xl bg-amber-400 text-white flex items-center justify-center"><Lightbulb size={18} /></span>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-amber-600">Why these connect</div>
                  <p className="text-sm text-[#334155] mt-0.5">{insight}</p>
                </div>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Business */}
              <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="client-business-track">
                <div className="h-1.5 bg-[#1D4ED8]" />
                <div className="p-5">
                  <h2 className="text-sm font-black uppercase tracking-wider text-[#1D4ED8] flex items-center gap-2"><Briefcase size={16} /> Business Blueprint</h2>
                  <div className="mt-4 flex items-center gap-4">
                    <Ring pct={biz.total ? Math.round((biz.blueprints_ready / biz.total) * 100) : 0} color="#1D4ED8" />
                    <div>
                      <div className="text-2xl font-black text-[#0A0F1C]">{biz.blueprints_ready}<span className="text-base text-[#94A3B8]"> / {biz.total} ready</span></div>
                      <div className="text-xs text-[#64748B]">{biz.total === 0 ? 'Not started yet' : `${biz.total} session${biz.total === 1 ? '' : 's'}`}</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {biz.sessions.slice(0, 4).map((s) => (
                      <div key={s.session_id} className="flex items-center gap-2.5 text-sm border border-[#F1F5F9] rounded-lg px-3 py-2">
                        {s.has_deliverable ? <FileText size={15} className="text-[#1D4ED8] shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
                        <span className="truncate flex-1 text-[#334155]">{s.business_name || 'Your business'}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.has_deliverable ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{s.has_deliverable ? 'Blueprint ready' : (s.status || 'in progress')}</span>
                      </div>
                    ))}
                    {biz.sessions.length === 0 && <div className="text-sm text-[#64748B] border border-dashed border-[#E2E8F0] rounded-xl p-4 text-center">Your strategist will start your Business Blueprint with you.</div>}
                  </div>
                </div>
              </div>

              {/* Nonprofit */}
              <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="client-nonprofit-track">
                <div className="h-1.5 bg-fuchsia-500" />
                <div className="p-5">
                  <h2 className="text-sm font-black uppercase tracking-wider text-fuchsia-700 flex items-center gap-2"><HeartHandshake size={16} /> Nonprofit Launch</h2>
                  <div className="mt-4 space-y-3">
                    <ToolGate tool="nonprofit" compact><>
                    {np.projects.map((p) => {
                      const pct = Math.round((p.days_done / p.days_total) * 100);
                      return (
                        <div key={p.project_id} className="border border-[#F1F5F9] rounded-xl p-3 flex items-center gap-4">
                          <Ring pct={pct} color="#C026D3" />
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-[#0A0F1C] truncate">{p.name}</div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#64748B] mt-0.5">
                              <span className="flex items-center gap-1"><Rocket size={11} /> {p.days_done}/90 days</span>
                              <span className="flex items-center gap-1"><CheckCircle2 size={11} /> Board-ready {p.readiness}%</span>
                              {p.incorp_total > 0 && <span className="flex items-center gap-1"><ClipboardCheck size={11} /> 501(c)(3) {p.incorp_done}/{p.incorp_total}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {np.projects.length === 0 && <StartNonprofit />}
                    </></ToolGate>
                  </div>
                </div>
              </div>
            </div>

            {/* Answer your Business Blueprint */}
            <div className="mt-6" data-testid="client-jumpstart-section">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center"><Briefcase size={16} /></span>
                <div>
                  <h2 className="text-base font-black text-[#0A0F1C]">Your Business Blueprint</h2>
                  <p className="text-xs text-[#64748B]">Answer your 7-Stage Jumpstart questions right here — at your own pace.</p>
                </div>
              </div>
              <ToolGate tool="jumpstart" compact><ClientJumpstart /></ToolGate>
            </div>

            {/* Website Vision */}
            <div className="mt-6" data-testid="client-website-vision-section">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1D4ED8] to-[#F97316] text-white flex items-center justify-center"><Globe size={16} /></span>
                <div>
                  <h2 className="text-base font-black text-[#0A0F1C]">Your Website Vision</h2>
                  <p className="text-xs text-[#64748B]">Tell us how you want your site to look & feel — we'll turn it into a build-ready brief.</p>
                </div>
              </div>
              <ToolGate tool="website_vision" compact><WebsiteVisionPanel base="/api/portal/website-vision" q="" /></ToolGate>
            </div>

            {/* Business Essentials */}
            <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="client-essentials">
              <ToolGate tool="clarity_hub" compact><VenturesPanel base="/api/portal/clarity-hub" q="" allowDelete /></ToolGate>
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
